/**
 * Schedule compare helpers — keep in sync with api/utils/scheduleAvailEquivalence.js
 * - 0.5 ≡ 1 for availability "on" fractions
 * - Collapse consecutive Until pairs only when the value is really the same
 * - Compact schedules are also compared hour by hour
 */

const AVAIL_ON_FRACTIONS = new Set([0.5, 1]);

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || /^(Through|For|Until)\b/i.test(trimmed) || /^Until:/i.test(trimmed)) {
      return null;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const isAvailOnFraction = (value: unknown): boolean => {
  const n = toFiniteNumber(value);
  return n != null && AVAIL_ON_FRACTIONS.has(n);
};

export const isNumericScheduleValue = (field: unknown): boolean => {
  if (Array.isArray(field)) {
    return field.length > 0 && field.every((item) => toFiniteNumber(item) != null);
  }
  return toFiniteNumber(field) != null;
};

const isUntilField = (field: unknown): boolean =>
  typeof field === 'string' && /^Until:\s*/i.test(field.trim());

/** True when both sides are availability "on" fractions (0.5 or 1). */
export const availScheduleFieldsEquivalent = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && !Array.isArray(b)) {
    return a.some((item) => availScheduleFieldsEquivalent(item, b));
  }
  if (!Array.isArray(a) && Array.isArray(b)) {
    return b.some((item) => availScheduleFieldsEquivalent(a, item));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => availScheduleFieldsEquivalent(item, b[i]));
  }
  return isAvailOnFraction(a) && isAvailOnFraction(b);
};

/**
 * Consecutive Until values merge only when they are the same value for the whole run.
 * A mixed allowed list does not collapse: 24 then [24, 25] stays two segments.
 */
export const scheduleValuesEqualForCollapse = (a: unknown, b: unknown): boolean => {
  if (availScheduleFieldsEquivalent(a, b)) return true;

  const n1 = toFiniteNumber(a);
  const n2 = toFiniteNumber(b);
  if (n1 != null && n2 != null) {
    return n1 === n2;
  }
  if (!Array.isArray(a) && Array.isArray(b)) {
    return b.length > 0 && b.every((item) => scheduleValuesEqualForCollapse(a, item));
  }
  if (Array.isArray(a) && !Array.isArray(b)) {
    return a.length > 0 && a.every((item) => scheduleValuesEqualForCollapse(item, b));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length === b.length && a.every((item, i) => scheduleValuesEqualForCollapse(item, b[i]))) {
      return true;
    }
    return a.length > 0 && b.length > 0
      && a.every((left) => b.every((right) => scheduleValuesEqualForCollapse(left, right)));
  }
  return false;
};

const isThroughField = (field: unknown): boolean =>
  typeof field === 'string' && /^Through:\s*/i.test(field.trim());

const isForField = (field: unknown): boolean =>
  typeof field === 'string' && /^For:\s*/i.test(field.trim());

const untilMinutes = (field: unknown): number | null => {
  if (typeof field !== 'string') return null;
  const match = field.trim().match(/^Until:\s*(\d{1,2}):(\d{2})$/i);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes !== 0)) return null;
  return hours * 60 + minutes;
};

/** A code array is a list of allowed numbers, not a min/max range. */
export const revisionValueAllowedByCode = (revisionValue: unknown, codeValue: unknown): boolean => {
  if (availScheduleFieldsEquivalent(revisionValue, codeValue)) return true;

  const revisionNumber = toFiniteNumber(revisionValue);
  const codeNumber = toFiniteNumber(codeValue);
  if (revisionNumber != null && codeNumber != null) return revisionNumber === codeNumber;

  if (!Array.isArray(revisionValue) && Array.isArray(codeValue)) {
    return codeValue.some((item) => revisionValueAllowedByCode(revisionValue, item));
  }
  if (Array.isArray(revisionValue) && !Array.isArray(codeValue)) {
    return revisionValue.length > 0
      && revisionValue.every((item) => revisionValueAllowedByCode(item, codeValue));
  }
  if (Array.isArray(revisionValue) && Array.isArray(codeValue)) {
    return revisionValue.length === codeValue.length
      && revisionValue.every((item, index) => revisionValueAllowedByCode(item, codeValue[index]));
  }
  return revisionValue === codeValue;
};

export type ScheduleDataItem = { field?: unknown };

type ScheduleSegment = { end: number; value: unknown; valueIndex?: number };
type ScheduleBlock = { through: string; dayType: string; segments: ScheduleSegment[] };

const parseCompactSchedule = (
  dataArray: ScheduleDataItem[] | null | undefined,
  normalizeField?: (field: unknown) => unknown
): ScheduleBlock[] | null => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) return null;
  const normalize = normalizeField || ((field: unknown) => field);
  const blocks: ScheduleBlock[] = [];
  let through = '';
  let dayType = '';
  let segments: ScheduleSegment[] = [];
  let sawScheduleField = false;

  const flush = () => {
    if (segments.length === 0) return;
    blocks.push({ through, dayType, segments });
    segments = [];
  };

  for (let i = 0; i < dataArray.length; i += 1) {
    const field = normalize(dataArray[i]?.field);
    if (isThroughField(field)) {
      flush();
      through = String(field).trim();
      dayType = '';
      sawScheduleField = true;
      continue;
    }
    if (isForField(field)) {
      flush();
      dayType = String(field).trim();
      sawScheduleField = true;
      continue;
    }
    const end = untilMinutes(field);
    if (end != null) {
      const value = normalize(dataArray[i + 1]?.field);
      if (!isNumericScheduleValue(value)) return null;
      if (segments.length > 0 && end <= segments[segments.length - 1].end) return null;
      segments.push({ end, value, valueIndex: i + 1 });
      i += 1;
      sawScheduleField = true;
      continue;
    }
    return null;
  }
  flush();
  if (!sawScheduleField || blocks.length === 0) return null;
  return blocks;
};

const valueCoveringMinute = (segments: ScheduleSegment[], minute: number): unknown => {
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i].end >= minute) return segments[i].value;
  }
  return undefined;
};

const daySlicesAllowed = (revisionSegments: ScheduleSegment[], codeSegments: ScheduleSegment[]): boolean => {
  const cuts = new Set<number>();
  revisionSegments.forEach((segment) => cuts.add(segment.end));
  codeSegments.forEach((segment) => cuts.add(segment.end));
  const minutes = Array.from(cuts).sort((a, b) => a - b);
  if (minutes.length === 0) return false;
  return minutes.every((minute) => {
    const revisionValue = valueCoveringMinute(revisionSegments, minute);
    const codeValue = valueCoveringMinute(codeSegments, minute);
    if (revisionValue === undefined || codeValue === undefined) return false;
    return revisionValueAllowedByCode(revisionValue, codeValue);
  });
};

const closestNumber = (values: unknown[], target: number): number | null => {
  let closest: number | null = null;
  let closestDistance = Infinity;
  values.forEach((item) => {
    const number = toFiniteNumber(item);
    if (number == null) return;
    const distance = Math.abs(number - target);
    if (distance < closestDistance) {
      closest = number;
      closestDistance = distance;
    }
  });
  return closest;
};

/** Index of each code list → closest allowed number for the revision hour. */
export const closestCodeArrayValues = (
  revisionData: ScheduleDataItem[] | null | undefined,
  codeData: ScheduleDataItem[] | null | undefined,
  normalizeField?: (field: unknown) => unknown
): Map<number, number> | null => {
  const revisionBlocks = parseCompactSchedule(revisionData, normalizeField);
  const codeBlocks = parseCompactSchedule(codeData, normalizeField);
  if (!revisionBlocks || !codeBlocks || revisionBlocks.length !== codeBlocks.length) return null;
  const replacements = new Map<number, number>();
  const aligned = codeBlocks.every((codeBlock, index) => {
    const revisionBlock = revisionBlocks[index];
    if (revisionBlock.through !== codeBlock.through || revisionBlock.dayType !== codeBlock.dayType) {
      return false;
    }
    codeBlock.segments.forEach((segment) => {
      if (!Array.isArray(segment.value)) return;
      const revisionValue = valueCoveringMinute(revisionBlock.segments, segment.end);
      const target = toFiniteNumber(revisionValue);
      if (target == null) return;
      const closest = closestNumber(segment.value, target);
      if (closest == null || segment.valueIndex == null) return;
      replacements.set(segment.valueIndex, closest);
    });
    return true;
  });
  return aligned ? replacements : null;
};

/**
 * Compare two Schedule:Compact payloads by calendar block and hour.
 * Returns null when either side is not a pure compact schedule.
 */
export const compareCompactScheduleData = (
  revisionData: ScheduleDataItem[] | null | undefined,
  codeData: ScheduleDataItem[] | null | undefined,
  normalizeField?: (field: unknown) => unknown
): boolean | null => {
  const revisionBlocks = parseCompactSchedule(revisionData, normalizeField);
  const codeBlocks = parseCompactSchedule(codeData, normalizeField);
  if (!revisionBlocks || !codeBlocks) return null;
  if (revisionBlocks.length !== codeBlocks.length) return false;
  return revisionBlocks.every((revisionBlock, index) => {
    const codeBlock = codeBlocks[index];
    if (revisionBlock.through !== codeBlock.through) return false;
    if (revisionBlock.dayType !== codeBlock.dayType) return false;
    return daySlicesAllowed(revisionBlock.segments, codeBlock.segments);
  });
};

/**
 * Collapse consecutive Until + same-value pairs into the last Until + value.
 * Example: Until: 16:00, 24, Until: 24:00, 24 → Until: 24:00, 24
 */
export const normalizeScheduleDataForCompare = <T extends ScheduleDataItem>(
  dataArray: T[] | null | undefined
): T[] | null | undefined => {
  if (!Array.isArray(dataArray)) return dataArray;
  const out: T[] = [];
  let i = 0;
  while (i < dataArray.length) {
    const item = dataArray[i];
    const field = item?.field;
    const next = dataArray[i + 1];
    if (isUntilField(field) && next && isNumericScheduleValue(next.field)) {
      let lastUntil = item;
      let lastValue = next;
      let j = i + 2;
      while (
        j + 1 < dataArray.length &&
        isUntilField(dataArray[j]?.field) &&
        isNumericScheduleValue(dataArray[j + 1]?.field) &&
        scheduleValuesEqualForCollapse(lastValue.field, dataArray[j + 1].field)
      ) {
        lastUntil = dataArray[j];
        lastValue = dataArray[j + 1];
        j += 2;
      }
      out.push(lastUntil);
      out.push(lastValue);
      i = j;
      continue;
    }
    out.push(item);
    i += 1;
  }
  return out;
};
